import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar, Link2, Copy, ExternalLink, Badge, ChevronLeft, ChevronRight } from 'lucide-react';
import { clientStockStyle } from '../lib/cardStock';
import { useAuthStore } from '../store/authStore';
import { useAppStore } from '../store/appStore';
import { toast } from 'sonner';
import { BACKEND_URL } from '../lib/api';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/calendar.css';

const API = BACKEND_URL;
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(BigCalendar);

// Statuses considered as "confirmed/booked"
const CONFIRMED_STATUSES = ['confirmed', 'booked', 'in_progress', 'completed', 'invoiced', 'paid'];

const PHASE_LABELS = {
  'pre': 'Pre',
  'production': 'Prod',
  'post': 'Post',
  'travel': 'Travel',
  'wet_weather': 'W/W',
};

export default function JobCalendarEnhanced({ jobs }) {
  const navigate = useNavigate();
  const { sessionToken } = useAuthStore();
  const { updateJobInList } = useAppStore();
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // First day of week preference
  const [firstDayOfWeek, setFirstDayOfWeek] = useState(() => {
    return localStorage.getItem('calendarFirstDay') || 'sunday';
  });
  
  useEffect(() => {
    localStorage.setItem('calendarFirstDay', firstDayOfWeek);
    // Update moment locale to respect first day of week
    if (firstDayOfWeek === 'monday') {
      moment.updateLocale('en', {
        week: {
          dow: 1, // Monday is the first day of the week
        }
      });
    } else {
      moment.updateLocale('en', {
        week: {
          dow: 0, // Sunday is the first day of the week
        }
      });
    }
  }, [firstDayOfWeek]);

  // Each job is printed on its client's card stock, so a client's jobs match
  // their job cards (lib/cardStock). Returns --stock / --stock-ink CSS vars.
  const jobColorMap = useMemo(() => {
    const map = {};
    jobs.forEach((job) => {
      map[job.id] = clientStockStyle(job.client);
    });
    return map;
  }, [jobs]);

  // Build calendar events from job phases AND rental periods
  const calendarEvents = useMemo(() => {
    const events = [];
    
    jobs.forEach((job) => {
      // Skip jobs with 'lost' status
      if (job.status === 'lost') return;
      
      // Add rental period event for rental-only jobs
      if (job.equipment_rental_only && job.rental_start_date && job.rental_end_date) {
        const startDate = new Date(job.rental_start_date);
        const endDate = new Date(job.rental_end_date);
        endDate.setHours(23, 59, 59, 999);
        const isConfirmed = CONFIRMED_STATUSES.includes(job.status);
        const color = jobColorMap[job.id];
        events.push({
          id: `${job.id}-rental`,
          title: `Rental | ${job.project_name || 'Untitled'}`,
          start: startDate,
          end: endDate,
          resource: {
            jobId: job.id,
            phaseIndex: -1,
            phaseType: 'rental',
            phaseLabel: '',
            jobName: job.project_name || 'Untitled Job',
            clientName: job.client || '',
            totalDays: Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1,
            status: job.status,
            isConfirmed: isConfirmed,
            color: color,
            isRental: true,
          }
        });
        return; // rental-only jobs don't have phases to render
      }
      
      if (!job.phases) return;
      
      job.phases.forEach((phase, phaseIndex) => {
        if (!phase.start_date) return;
        
        const startDate = new Date(phase.start_date);
        const days = Math.ceil(phase.estimated_days || 1);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + days - 1);
        // Set end to end of day for proper spanning
        endDate.setHours(23, 59, 59, 999);
        
        const isConfirmed = CONFIRMED_STATUSES.includes(job.status);
        const color = jobColorMap[job.id];
        
        events.push({
          id: `${job.id}-${phaseIndex}`,
          title: `${PHASE_LABELS[phase.type] || phase.type}${phase.label ? ' | ' + phase.label : ''} - ${job.project_name || 'Untitled'}`,
          start: startDate,
          end: endDate,
          resource: {
            jobId: job.id,
            phaseIndex: phaseIndex,
            phaseType: phase.type,
            phaseLabel: phase.label,
            jobName: job.project_name || 'Untitled Job',
            clientName: job.client || '',
            totalDays: days,
            status: job.status,
            isConfirmed: isConfirmed,
            color: color,
          }
        });
      });
    });
    
    return events;
  }, [jobs, jobColorMap]);

  // Update phase dates on backend
  const updatePhaseDate = useCallback(async (jobId, phaseIndex, newStartDate, newDuration) => {
    try {
      const response = await fetch(`${API}/api/jobs/${jobId}/phases/${phaseIndex}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          start_date: moment(newStartDate).format('YYYY-MM-DD'),
          estimated_days: newDuration
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update phase');
      }
      
      // Update only the affected job without scrolling
      await updateJobInList(jobId);
      toast.success('Phase updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update phase');
      console.error('Error updating phase:', error);
      // Refresh job to revert optimistic update
      updateJobInList(jobId);
    }
  }, [sessionToken, updateJobInList]);

  // Handle drag start
  const onDragStart = useCallback(({ event }) => {
    setDraggingEventId(event.id);
    // Delay the ghost effect slightly so the drag preview renders first
    setTimeout(() => {
      setIsDragMoving(true);
    }, 50);
  }, []);

  // Handle event drop (drag and drop)
  const onEventDrop = useCallback(({ event, start, end }) => {
    const { jobId, phaseIndex, totalDays } = event.resource;
    setDraggingEventId(null); // Clear dragging state
    setIsDragMoving(false);
    updatePhaseDate(jobId, phaseIndex, start, totalDays);
  }, [updatePhaseDate]);

  // Handle event resize
  const onEventResize = useCallback(({ event, start, end }) => {
    const { jobId, phaseIndex } = event.resource;
    // Calculate new duration in days
    // end is the end of the last day (23:59:59), start is beginning of first day
    // For a 2-day event: start=Dec10 00:00, end=Dec11 23:59
    // Difference in ms / ms per day ≈ 2 days, round to get exact days
    const startOfDay = new Date(start);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(end);
    endOfDay.setHours(0, 0, 0, 0);
    
    const daysDifference = Math.round((endOfDay - startOfDay) / (1000 * 60 * 60 * 24));
    const duration = daysDifference + 1; // +1 because we include both start and end days
    
    // Clear ghost state after resize
    setDraggingEventId(null);
    setIsDragMoving(false);
    
    updatePhaseDate(jobId, phaseIndex, start, duration);
  }, [updatePhaseDate]);

  // Handle event click - navigate to job detail
  const onSelectEvent = useCallback((event) => {
    navigate(`/jobs/${event.resource.jobId}`);
  }, [navigate]);

  // Track dragging state
  const [draggingEventId, setDraggingEventId] = useState(null);
  const [isDragMoving, setIsDragMoving] = useState(false);

  // Custom event style getter
  const eventStyleGetter = useCallback((event) => {
    const { color, isConfirmed } = event.resource;
    const isDragging = event.id === draggingEventId && isDragMoving;
    
    // Stock colours, the dashed border and the hatch for jobs not booked yet
    // live in styles/calendar.css (.calendar-event-stock, .calendar-unbooked).
    return {
      className: isConfirmed ? 'calendar-event-stock' : 'calendar-event-stock calendar-unbooked',
      style: {
        ...color,
        padding: '2px 6px',
        fontSize: '13px',
        cursor: 'pointer',
        opacity: isDragging ? 0.6 : (isConfirmed ? 1 : 0.75),
        filter: isDragging ? 'grayscale(50%)' : 'none',
        transition: 'opacity 0.1s ease, filter 0.1s ease',
      }
    };
  }, [draggingEventId, isDragMoving]);

  // Get subscription URL for calendar sync
  const [subscriptionUrl, setSubscriptionUrl] = useState(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  const handleSubscribe = async () => {
    try {
      const response = await fetch(`${API}/api/calendar/subscription-url`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get subscription URL');
      }
      
      const data = await response.json();
      setSubscriptionUrl(data.subscription_url);
      setShowSubscribeModal(true);
    } catch (error) {
      toast.error(error.message || 'Failed to get subscription URL');
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(subscriptionUrl);
      toast.success('URL copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  // Custom event wrapper to handle drag issues in popup
  const EventWrapper = useCallback(({ event, children }) => {
    const handleDragStart = (e) => {
      // Check if we're in a popup overlay
      if (e.target.closest('.rbc-overlay')) {
        e.preventDefault();
        e.stopPropagation();
        toast.info('Please close the popup to drag events');
        return false;
      }
    };

    return (
      <div onDragStart={handleDragStart}>
        {children}
      </div>
    );
  }, []);

  // Custom toolbar component
  const CustomToolbar = useCallback((toolbar) => {
    const goToBack = () => {
      toolbar.onNavigate('PREV');
    };

    const goToNext = () => {
      toolbar.onNavigate('NEXT');
    };

    const goToToday = () => {
      toolbar.onNavigate('TODAY');
    };

    return (
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        {/* Left side - Action buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="h-9" onClick={goToToday}>
            Today
          </Button>
          <Select value={firstDayOfWeek} onValueChange={setFirstDayOfWeek}>
            <SelectTrigger className="w-[120px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sunday">Sun start</SelectItem>
              <SelectItem value="monday">Mon start</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            className="h-9"
            onClick={handleSubscribe}
            title="Subscribe in Google Calendar / Apple Calendar"
          >
            <Link2 className="w-4 h-4" />
            Subscribe
          </Button>
        </div>
        
        {/* Right side - Month navigation */}
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9 w-9 p-0" onClick={goToBack} aria-label="Previous month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xl min-w-[160px] sm:min-w-[180px] text-center">
            {toolbar.label}
          </span>
          <Button variant="outline" className="h-9 w-9 p-0" onClick={goToNext} aria-label="Next month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }, [firstDayOfWeek, setFirstDayOfWeek, handleSubscribe]);

  // Check if there are any events
  const hasAnyEvents = calendarEvents.length > 0;

  return (
    <>
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-[18px] h-[18px]" />
          Schedule
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasAnyEvents && (
          <div className="text-center py-4 mb-4 bg-mist rounded-cards">
            <p className="text-sm text-muted-foreground">
              No scheduled phases yet. Add start dates to your job phases to see them on the calendar.
            </p>
          </div>
        )}
        
        {/* React Big Calendar */}
        <div className="calendar-container" style={{ height: '600px' }}>
          <DnDCalendar
            key={`calendar-${firstDayOfWeek}`}
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            date={currentDate}
            onNavigate={setCurrentDate}
            view="month"
            views={['month']}
            culture={firstDayOfWeek === 'monday' ? 'en-GB' : 'en-US'}
            onSelectEvent={onSelectEvent}
            onDragStart={onDragStart}
            onEventDrop={onEventDrop}
            onEventResize={onEventResize}
            eventPropGetter={eventStyleGetter}
            draggableAccessor={(event) => !event.resource?.isRental}
            resizable
            popup
            components={{
              toolbar: CustomToolbar,
              eventWrapper: EventWrapper
            }}
            formats={{
              dayFormat: (date, culture, localizer) =>
                localizer.format(date, 'ddd', culture),
            }}
            style={{ height: '100%' }}
          />
        </div>

        {/* Legend */}
        {hasAnyEvents && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-muted-foreground">Jobs:</span>
              {jobs.filter(job => job.status !== 'lost' && job.status !== 'completed' && (job.phases?.some(p => p.start_date) || (job.equipment_rental_only && job.rental_start_date))).map((job) => {
                const color = jobColorMap[job.id];
                return (
                  <div
                    key={job.id}
                    className="on-stock flex items-center gap-1.5 text-xs px-3 py-1 rounded-pill cursor-pointer hover:opacity-80"
                    style={color}
                    onClick={() => navigate(`/jobs/${job.id}`)}
                  >
                    <span>{job.project_name?.slice(0, 20) || 'Untitled'}</span>
                    {job.status === 'draft' && (
                      <span className="text-[10px] leading-none px-1.5 py-0.5 border border-current rounded-pill">Draft</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="calendar-unbooked w-6 h-4 rounded-small bg-mist" />
              <span>Job not booked yet</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Subscribe Modal */}
    <Dialog open={showSubscribeModal} onOpenChange={setShowSubscribeModal}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Subscribe to calendar
          </DialogTitle>
          <DialogDescription>
            Add your No Half Days schedule to Google Calendar, Apple Calendar, or Outlook. 
            Your calendar will automatically sync when you make changes.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Subscription URL</label>
            <div className="flex gap-2">
              <Input 
                value={subscriptionUrl || ''} 
                readOnly 
                className="text-xs"
              />
              <Button variant="outline" size="icon" onClick={copyToClipboard}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t border-line">
            <p className="text-sm font-medium">How to subscribe:</p>
            
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <span className="font-medium text-foreground min-w-[100px]">Google Calendar:</span>
                <span>Settings (gear icon) → Add calendar → From URL → Paste the URL</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-medium text-foreground min-w-[100px]">Apple Calendar:</span>
                <span>File → New Calendar Subscription → Paste the URL</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-medium text-foreground min-w-[100px]">Outlook:</span>
                <span>Add calendar → Subscribe from web → Paste the URL</span>
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => window.open(`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(subscriptionUrl)}`, '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
              Open in Google Calendar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
